// src/pages/Partner/PartnerLearnerDetails.jsx

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Activity,
  ArrowLeft,
  Award,
  BarChart3,
  BriefcaseBusiness,
  ListChecks,
  RefreshCw,
  ShieldAlert,
  UserCog,
  UserRound,
  X,
} from "lucide-react";

import {
  Link,
  useOutletContext,
  useParams,
} from "react-router-dom";

import {
  fetchPartnerLearnerDetail,
  fetchPartnerLearnerProfileSummary,
  fetchPartnerLearnerQuestProgress,
  fetchPartnerLearnerSubquestProgress,
  fetchPartnerLearnerTasks,
} from "../../lib/partner/partnerProgress";

import {
  fetchPartnerCurriculumCatalog,
} from "../../lib/partner/partnerCurriculum";

import {
  fetchPartnerLearnerFacilitator,
  fetchPartnerLearnerFacilitatorOptions,
  removePartnerLearnerFacilitator,
  setPartnerLearnerFacilitator,
} from "../../lib/partner/partnerFacilitators";

import PartnerLearnerFinancialMetrics from "../../components/partner/PartnerLearnerFinancialMetrics";


/* -------------------------------------------------------------------------- */
/* Feature flags                                                              */
/* -------------------------------------------------------------------------- */

/*
 * IMPORTANT:
 *
 * The previous detailed Partner learner page has NOT
 * been deleted.
 *
 * It remains fully implemented further down this file.
 *
 * Set this to true whenever we want to expose the
 * detailed/advanced learner view again.
 */

const SHOW_LEGACY_LEARNER_DETAILS =
  false;


/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function numberValue(value) {
  const parsed =
    Number(value);

  return Number.isFinite(
    parsed
  )
    ? parsed
    : 0;
}


function displayValue(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return "Not submitted";
  }

  return String(value);
}


function displayBoolean(value) {
  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  return "Not submitted";
}


function formatDateTime(value) {
  if (!value) {
    return "Not available";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}


function positionLabel(
  detail,
  questProgress = []
) {
  if (
    !detail.current_quest_id &&
    !detail.current_subquest_id &&
    !detail.current_level_key
  ) {
    return "Not started";
  }

  const currentQuestId =
    String(
      detail.current_quest_id ||
        ""
    ).trim();


  const currentQuest =
    questProgress.find(
      (quest) =>
        String(
          quest.quest_id ||
            ""
        ).trim() ===
        currentQuestId
    );


  const currentQuestLabel =
    currentQuestId
      ? [
          `Quest ${currentQuestId}`,

          currentQuest
            ?.quest_title ||
            null,
        ]
          .filter(Boolean)
          .join(" — ")
      : null;


  return [
    currentQuestLabel,

    detail.current_subquest_id
      ? `Subquest ${detail.current_subquest_id}`
      : null,

    detail.current_level_key ||
      null,
  ]
    .filter(Boolean)
    .join(" · ");
}


function statusClasses(status) {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800";

    case "active":
    case "unlocked":
      return "bg-amber-100 text-amber-800";

    default:
      return "bg-gray-200 text-gray-700";
  }
}


function clampPercentage(
  value
) {
  return Math.max(
    0,
    Math.min(
      100,
      numberValue(value)
    )
  );
}


function learnerActivityStatus(
  detail
) {
  const rawDays =
    detail
      ?.days_since_activity;


  if (
    rawDays === null ||
    rawDays === undefined ||
    rawDays === ""
  ) {
    return {
      label:
        "Not started",

      detail:
        "No activity recorded",
    };
  }


  const days =
    Number(rawDays);


  if (
    !Number.isFinite(days)
  ) {
    return {
      label:
        "Not available",

      detail:
        "Activity information unavailable",
    };
  }


  if (days >= 7) {
    return {
      label:
        "Inactive",

      detail:
        `Last active ${days} ${
          days === 1
            ? "day"
            : "days"
        } ago`,
    };
  }


  if (days >= 3) {
    return {
      label:
        "Watch",

      detail:
        `Last active ${days} days ago`,
    };
  }


  if (days <= 0) {
    return {
      label:
        "Active",

      detail:
        "Active today",
    };
  }


  if (days === 1) {
    return {
      label:
        "Active",

      detail:
        "Last active yesterday",
    };
  }


  return {
    label:
      "Active",

    detail:
      `Last active ${days} days ago`,
  };
}


function formatRelativeActivity(
  value
) {
  if (!value) {
    return "No activity yet";
  }


  const date =
    new Date(value);


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "Not available";
  }


  const difference =
    Date.now() -
    date.getTime();


  const days =
    Math.max(
      0,
      Math.floor(
        difference /
          86400000
      )
    );


  if (days === 0) {
    return "Today";
  }


  if (days === 1) {
    return "Yesterday";
  }


  return `${days} days ago`;
}


/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function PartnerLearnerDetails() {
  const {
    cohortLearnerId,
  } = useParams();


  const {
    access,
  } =
    useOutletContext();


  /* ------------------------------------------------------------------------ */
  /* Learner data                                                             */
  /* ------------------------------------------------------------------------ */

  const [
    detail,
    setDetail,
  ] = useState(null);


  const [
    profile,
    setProfile,
  ] = useState(null);


  const [
    curriculumCatalog,
    setCurriculumCatalog,
  ] = useState([]);


  const [
    subquestProgress,
    setSubquestProgress,
  ] = useState([]);


  const [
    curriculumError,
    setCurriculumError,
  ] = useState("");


  const [
    questProgress,
    setQuestProgress,
  ] = useState([]);


  const [
    tasks,
    setTasks,
  ] = useState([]);


  /* ------------------------------------------------------------------------ */
  /* Facilitator                                                              */
  /* ------------------------------------------------------------------------ */

  const [
    facilitator,
    setFacilitator,
  ] = useState(null);


  const [
    facilitatorOptions,
    setFacilitatorOptions,
  ] = useState([]);


  const [
    selectedFacilitatorId,
    setSelectedFacilitatorId,
  ] = useState("");


  const [
    facilitatorLoading,
    setFacilitatorLoading,
  ] = useState(true);


  const [
    facilitatorSaving,
    setFacilitatorSaving,
  ] = useState(false);


  const [
    facilitatorError,
    setFacilitatorError,
  ] = useState("");


  const [
    facilitatorMessage,
    setFacilitatorMessage,
  ] = useState("");


  const [
    facilitatorManagerOpen,
    setFacilitatorManagerOpen,
  ] = useState(false);


  const canManageFacilitator =
    [
      "owner",
      "admin",
      "cohort_manager",
    ].includes(
      access?.partner_role
    );


  /* ------------------------------------------------------------------------ */
  /* General loading/error                                                    */
  /* ------------------------------------------------------------------------ */

  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    error,
    setError,
  ] = useState("");


  /* ------------------------------------------------------------------------ */
  /* Load learner                                                             */
  /* ------------------------------------------------------------------------ */

  const loadLearner =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          setError(
            ""
          );

          setCurriculumError(
            ""
          );


          /*
           * Keep the legacy detail call because
           * the hidden legacy UI still uses it.
           *
           * The new simplified profile uses its
           * own purpose-built RPC.
           */
          const [
            detailRow,
            profileRow,
          ] =
            await Promise.all(
              [
                fetchPartnerLearnerDetail(
                  cohortLearnerId
                ),

                fetchPartnerLearnerProfileSummary(
                  cohortLearnerId
                ),
              ]
            );


          if (
            !detailRow ||
            !profileRow
          ) {
            throw new Error(
              "The entrepreneur could not be found or you do not have access to their cohort."
            );
          }


          setDetail(
            detailRow
          );


          setProfile(
            profileRow
          );


          /*
           * Load the old detailed data as well as
           * the new subquest-level progress.
           */
          const [
            questResult,
            taskResult,
            subquestResult,
            catalogResult,
          ] =
            await Promise.allSettled(
              [
                fetchPartnerLearnerQuestProgress(
                  cohortLearnerId
                ),

                fetchPartnerLearnerTasks(
                  cohortLearnerId
                ),

                fetchPartnerLearnerSubquestProgress(
                  cohortLearnerId
                ),

                fetchPartnerCurriculumCatalog({
                  cohortId:
                    profileRow.cohort_id,
                }),
              ]
            );


          if (
            questResult.status ===
            "fulfilled"
          ) {
            setQuestProgress(
              Array.isArray(
                questResult.value
              )
                ? questResult.value
                : []
            );
          } else {
            console.error(
              "Legacy quest progress loading failed:",
              questResult.reason
            );

            setQuestProgress(
              []
            );
          }


          if (
            taskResult.status ===
            "fulfilled"
          ) {
            setTasks(
              Array.isArray(
                taskResult.value
              )
                ? taskResult.value
                : []
            );
          } else {
            console.error(
              "Legacy learner tasks loading failed:",
              taskResult.reason
            );

            setTasks(
              []
            );
          }


          if (
            subquestResult.status ===
            "fulfilled"
          ) {
            setSubquestProgress(
              Array.isArray(
                subquestResult.value
              )
                ? subquestResult.value
                : []
            );
          } else {
            console.error(
              "Subquest progress loading failed:",
              subquestResult.reason
            );

            setSubquestProgress(
              []
            );

            setCurriculumError(
              subquestResult.reason
                ?.message ||
                "The entrepreneur's curriculum progress could not be loaded."
            );
          }


          if (
            catalogResult.status ===
            "fulfilled"
          ) {
            setCurriculumCatalog(
              Array.isArray(
                catalogResult.value
              )
                ? catalogResult.value
                : []
            );
          } else {
            console.error(
              "Partner curriculum loading failed:",
              catalogResult.reason
            );

            setCurriculumCatalog(
              []
            );

            setCurriculumError(
              catalogResult.reason
                ?.message ||
                "The cohort curriculum could not be loaded."
            );
          }
        } catch (
          loadError
        ) {
          console.error(
            "Partner entrepreneur detail loading failed:",
            loadError
          );


          setError(
            loadError?.message ||
              "The entrepreneur details could not be loaded."
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        cohortLearnerId,
      ]
    );


  useEffect(() => {
    loadLearner();
  }, [loadLearner]);


  /* ------------------------------------------------------------------------ */
  /* Load facilitator                                                         */
  /* ------------------------------------------------------------------------ */

  const loadFacilitator =
    useCallback(
      async () => {
        try {
          setFacilitatorLoading(
            true
          );

          setFacilitatorError(
            ""
          );


          const current =
            await fetchPartnerLearnerFacilitator(
              cohortLearnerId
            );


          setFacilitator(
            current
          );


          setSelectedFacilitatorId(
            current
              ?.partner_member_id ||
              ""
          );


          if (
            canManageFacilitator
          ) {
            const options =
              await fetchPartnerLearnerFacilitatorOptions(
                cohortLearnerId
              );


            setFacilitatorOptions(
              Array.isArray(
                options
              )
                ? options
                : []
            );
          } else {
            setFacilitatorOptions(
              []
            );
          }
        } catch (
          loadError
        ) {
          console.error(
            "Loading entrepreneur facilitator failed:",
            loadError
          );


          setFacilitatorError(
            loadError?.message ||
              "The facilitator assignment could not be loaded."
          );
        } finally {
          setFacilitatorLoading(
            false
          );
        }
      },
      [
        cohortLearnerId,
        canManageFacilitator,
      ]
    );


  useEffect(() => {
    loadFacilitator();
  }, [loadFacilitator]);


  /* ------------------------------------------------------------------------ */
  /* Save facilitator                                                         */
  /* ------------------------------------------------------------------------ */

  async function handleFacilitatorSave(
    event
  ) {
    event.preventDefault();


    const currentFacilitatorId =
      facilitator
        ?.partner_member_id ||
      "";


    if (
      selectedFacilitatorId ===
      currentFacilitatorId
    ) {
      setFacilitatorMessage(
        "No facilitator changes to save."
      );

      return;
    }


    try {
      setFacilitatorSaving(
        true
      );

      setFacilitatorError(
        ""
      );

      setFacilitatorMessage(
        ""
      );


      if (
        selectedFacilitatorId
      ) {
        await setPartnerLearnerFacilitator({
          cohortLearnerId,

          facilitatorPartnerMemberId:
            selectedFacilitatorId,
        });


        await loadFacilitator();


        setFacilitatorMessage(
          "Facilitator assigned successfully."
        );
      } else {
        await removePartnerLearnerFacilitator(
          cohortLearnerId
        );


        await loadFacilitator();


        setFacilitatorMessage(
          "Facilitator removed from this entrepreneur."
        );
      }
    } catch (
      saveError
    ) {
      console.error(
        "Saving entrepreneur facilitator failed:",
        saveError
      );


      setFacilitatorError(
        saveError?.message ||
          "The facilitator assignment could not be saved."
      );
    } finally {
      setFacilitatorSaving(
        false
      );
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Loading                                                                  */
  /* ------------------------------------------------------------------------ */

  if (loading) {
    return (
      <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center text-sm font-semibold text-[#757575]">

        <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />


        <p className="mt-3">
          Loading entrepreneur details…
        </p>

      </div>
    );
  }


  /* ------------------------------------------------------------------------ */
  /* Error                                                                    */
  /* ------------------------------------------------------------------------ */

  if (
    error &&
    (
      !detail ||
      !profile
    )
  ) {
    return (
      <section className="rounded-[28px] border border-red-200 bg-white p-6 shadow-sm">

        <h1 className="text-xl font-black">
          Entrepreneur unavailable
        </h1>


        <p className="mt-3 text-sm text-red-700">
          {error}
        </p>


        <div className="mt-6 flex flex-wrap gap-3">

          <button
            type="button"
            onClick={
              loadLearner
            }
            className="inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </button>


          <Link
            to="/partner/cohorts"
            className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black"
          >
            Back to cohorts
          </Link>

        </div>

      </section>
    );
  }


  if (
    !detail ||
    !profile
  ) {
    return (
      <section className="rounded-[28px] border border-red-200 bg-white p-6 shadow-sm">

        <h1 className="text-xl font-black">
          Entrepreneur unavailable
        </h1>


        <p className="mt-3 text-sm text-red-700">
          The entrepreneur details could not be loaded.
        </p>


        <button
          type="button"
          onClick={
            loadLearner
          }
          className="mt-6 inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </button>

      </section>
    );
  }


  /* ------------------------------------------------------------------------ */
  /* Derived values                                                           */
  /* ------------------------------------------------------------------------ */

  const progress =
  clampPercentage(
    profile
      ?.overall_progress_percent
  );


const activity =
  learnerActivityStatus(
    profile
  );


const facilitatorName =
  facilitatorLoading
    ? "Loading…"
    : facilitator
        ?.full_name ||
      facilitator
        ?.email ||
      "Unassigned";


const revenueLabel =
  "Monthly revenue range";


const revenueValue =
  profile
    ?.monthly_revenue_range;


  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <>

      {/* ==================================================================== */}
      {/* Back                                                                 */}
      {/* ==================================================================== */}

      <Link
        to={`/partner/cohorts/${profile.cohort_id}`}
        className="inline-flex items-center gap-2 text-sm font-black text-[#FF914D] transition hover:text-[#E9782F]"
      >
        <ArrowLeft className="h-4 w-4" />

        Back to cohort
      </Link>


      {/* ==================================================================== */}
      {/* Error                                                                */}
      {/* ==================================================================== */}

      {error && (
        <div
          role="alert"
          className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
        >
          {error}
        </div>
      )}


      {/* ==================================================================== */}
{/* Visible simplified profile                                           */}
{/* ==================================================================== */}

<section className="mt-6 rounded-[24px] border border-[#D7D7D7] bg-white p-6 shadow-sm md:p-8">

  <h1 className="text-[30px] font-black uppercase tracking-[-0.03em] text-[#FF914D] md:text-[36px]">
    {profile?.full_name ||
      "Entrepreneur"}
  </h1>


  <h2 className="mt-8 text-lg font-black uppercase tracking-[-0.01em] text-[#222222]">
    Profile
  </h2>


  <div className="mt-7 grid gap-x-14 gap-y-8 sm:grid-cols-2 xl:grid-cols-3">

    <div className="space-y-7">

      <SimpleProfileField
        label="Email"
        value={
          profile?.email
        }
      />


      <SimpleProfileField
        label="Phone"
        value={
          profile?.phone
        }
      />


      <SimpleProfileField
        label="Province"
        value={
          profile?.province
        }
      />

    </div>


    <div className="space-y-7">

      <SimpleProfileField
        label="Business name"
        value={
          profile?.business_name
        }
      />


      <SimpleProfileField
        label="Industry"
        value={
          profile?.industry
        }
      />


      <SimpleProfileField
        label="Legal nature"
        value={
          profile?.legal_nature
        }
      />

    </div>


    <div className="space-y-7">

      <SimpleProfileField
        label="Business age"
        value={
          profile?.business_age
        }
      />


      <SimpleProfileField
        label={
          revenueLabel
        }
        value={
          revenueValue
        }
      />

    </div>

  </div>

</section>


      {/* ==================================================================== */}
      {/* Visible simplified summary cards                                     */}
      {/* ==================================================================== */}

      <section className="mt-5 grid gap-4 md:grid-cols-3">

        <SimpleSummaryCard>

          <SimpleSummaryHeader
            icon={
              UserRound
            }
            label="Facilitator"
          />


          <p className="mt-6 text-[26px] font-black tracking-[-0.03em] text-black">
            {facilitatorName}
          </p>


          {facilitatorError && (
            <p className="mt-2 text-xs font-semibold text-red-600">
              {facilitatorError}
            </p>
          )}


          {canManageFacilitator && (
            <button
              type="button"
              onClick={() => {
                setFacilitatorMessage(
                  ""
                );

                setFacilitatorError(
                  ""
                );

                setFacilitatorManagerOpen(
                  true
                );
              }}
              className="mt-4 inline-flex items-center justify-center rounded-full bg-black px-5 py-2.5 text-xs font-black text-white transition hover:bg-[#292929]"
            >
              Manage
            </button>
          )}

        </SimpleSummaryCard>


        <SimpleSummaryCard>

          <SimpleSummaryHeader
            icon={
              Activity
            }
            label="Current Activity Status"
          />


          <p className="mt-6 text-[26px] font-black tracking-[-0.03em] text-black">
            {activity.label}
          </p>


          <p className="mt-2 text-sm font-medium text-[#757575]">
            {activity.detail}
          </p>

        </SimpleSummaryCard>


        <SimpleSummaryCard>

          <SimpleSummaryHeader
            icon={
              BarChart3
            }
            label="Current Progress"
          />


          <p className="mt-6 text-[26px] font-black tracking-[-0.03em] text-black">
            {Math.round(
              progress
            )}
            %
          </p>


          <p className="mt-2 text-sm font-medium text-[#757575]">
  {numberValue(
    profile?.levels_completed
  )}
  /
  {numberValue(
    profile?.total_levels
  )}{" "}
  levels completed
</p>

        </SimpleSummaryCard>

      </section>


      {/* ==================================================================== */}
{/* Visible curriculum progress                                          */}
{/* ==================================================================== */}

<section className="mt-10">

  <h2 className="text-[25px] font-black uppercase tracking-[-0.03em] text-[#FF914D] md:text-[30px]">
    Curriculum Progress
  </h2>


  <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575]">
    Progress across each quest and
    subquest currently available to this
    cohort.
  </p>


  {curriculumError && (
    <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
      {curriculumError}
    </div>
  )}


  {curriculumCatalog.length ===
  0 ? (
    <div className="mt-5 rounded-[22px] border border-dashed border-[#D7D7D7] bg-white p-8 text-center">

      <p className="font-black text-[#333333]">
        No curriculum assigned
      </p>


      <p className="mt-2 text-sm text-[#757575]">
        No curriculum is currently
        available for this cohort.
      </p>

    </div>
  ) : (
    <div className="mt-5 space-y-5">

      {curriculumCatalog.map(
        (quest) => {
          const questRows =
            (
              Array.isArray(
                quest.subquests
              )
                ? quest.subquests
                : []
            ).map(
              (subquest) => {
                const progressRow =
                  subquestProgress.find(
                    (row) =>
                      String(
                        row.quest_id
                      ) ===
                        String(
                          quest.id
                        ) &&
                      String(
                        row.subquest_id
                      ) ===
                        String(
                          subquest.id
                        )
                  );


                /*
                 * Sanity is the authoritative source
                 * for curriculum structure.
                 *
                 * The learner RPC provides completion
                 * state for that same quest/subquest.
                 */
                const curriculumLevelCount =
                  Array.isArray(
                    subquest.levels
                  )
                    ? subquest.levels
                        .length
                    : 0;


                const totalLevels =
                  curriculumLevelCount >
                  0
                    ? curriculumLevelCount
                    : numberValue(
                        progressRow
                          ?.total_levels
                      );


                const completedLevels =
                  Math.min(
                    totalLevels ||
                      Number.MAX_SAFE_INTEGER,

                    numberValue(
                      progressRow
                        ?.completed_levels
                    )
                  );


                const progressPercent =
                  totalLevels > 0
                    ? Math.min(
                        100,
                        Math.max(
                          0,
                          (
                            completedLevels /
                            totalLevels
                          ) *
                            100
                        )
                      )
                    : 0;


                return {
                  subquest,
                  progressRow,
                  totalLevels,
                  completedLevels,
                  progressPercent,
                };
              }
            );


          const questTotalLevels =
            questRows.reduce(
              (
                total,
                row
              ) =>
                total +
                row.totalLevels,
              0
            );


          const questCompletedLevels =
            questRows.reduce(
              (
                total,
                row
              ) =>
                total +
                row.completedLevels,
              0
            );


          const questProgressPercent =
            questTotalLevels > 0
              ? Math.min(
                  100,
                  Math.max(
                    0,
                    (
                      questCompletedLevels /
                      questTotalLevels
                    ) *
                      100
                  )
                )
              : 0;


          return (
            <article
              key={
                quest.id
              }
              className="overflow-hidden rounded-[22px] border border-[#D7D7D7] bg-white"
            >

              {/* ========================================================== */}
              {/* Quest                                                       */}
              {/* ========================================================== */}

              <header className="border-b border-[#E7E7E7] bg-[#FFF9F5] px-6 py-5">

                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">

                  <div className="min-w-0">

                    <p className="text-xs font-black uppercase tracking-[0.06em] text-[#FF914D]">
                      Quest {quest.id}
                    </p>


                    <h3 className="mt-1 text-xl font-black tracking-[-0.02em] text-[#222222]">
                      {quest.title ||
                        `Quest ${quest.id}`}
                    </h3>


                    <p className="mt-2 text-xs font-semibold text-[#757575]">
                      {questRows.length}{" "}
                      {questRows.length ===
                      1
                        ? "subquest"
                        : "subquests"}{" "}
                      ·{" "}
                      {questTotalLevels}{" "}
                      {questTotalLevels ===
                      1
                        ? "level"
                        : "levels"}
                    </p>

                  </div>


                  <div className="w-full sm:w-[190px]">

                    <div className="flex items-center justify-between gap-4">

                      <span className="text-xs font-bold text-[#757575]">
                        Quest progress
                      </span>


                      <span className="text-sm font-black text-[#333333]">
                        {questCompletedLevels}
                        /
                        {questTotalLevels}
                      </span>

                    </div>


                    <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#D7D7D7]">

                      <div
                        className="h-full rounded-full bg-[#FF914D]"
                        style={{
                          width: `${questProgressPercent}%`,
                        }}
                      />

                    </div>

                  </div>

                </div>

              </header>


              {/* ========================================================== */}
              {/* Subquests                                                   */}
              {/* ========================================================== */}

              <div className="overflow-x-auto">

                <table className="w-full min-w-[820px]">

                  <thead className="bg-[#F4F4F4]">

                    <tr>

                      <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                        Subquest
                      </th>


                      <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                        Levels completed
                      </th>


                      <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                        Last activity
                      </th>


                      <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                        Status
                      </th>

                    </tr>

                  </thead>


                  <tbody className="divide-y divide-[#E3E3E3]">

                    {questRows.map(
                      ({
                        subquest,
                        progressRow,
                        totalLevels,
                        completedLevels,
                        progressPercent,
                      }) => {
                        const isCurrent =
                          progressRow
                            ?.is_current_subquest ===
                          true;


                        const isCompleted =
                          totalLevels >
                            0 &&
                          completedLevels >=
                            totalLevels;


                        const hasStarted =
                          completedLevels >
                            0 ||
                          isCurrent;


                        const status =
                          isCurrent
                            ? "Current"
                            : isCompleted
                            ? "Completed"
                            : hasStarted
                            ? "In progress"
                            : "Not started";


                        return (
                          <tr
                            key={
                              subquest.id
                            }
                            className={[
                              "transition",

                              isCurrent
                                ? "bg-[#FFF9F5]"
                                : "hover:bg-[#FCFCFC]",
                            ].join(
                              " "
                            )}
                          >

                            {/* -------------------------------------------- */}
                            {/* Subquest                                     */}
                            {/* -------------------------------------------- */}

                            <td className="px-6 py-5">

                              <p className="text-xs font-black uppercase tracking-[0.04em] text-[#FF914D]">
                                Subquest{" "}
                                {
                                  subquest.id
                                }
                              </p>


                              <p className="mt-1 max-w-[340px] font-black leading-relaxed text-[#333333]">
                                {subquest.title ||
                                  `Subquest ${subquest.id}`}
                              </p>


                              {isCurrent &&
                                progressRow
                                  ?.current_level_key && (
                                  <p className="mt-2 text-xs font-semibold text-[#757575]">
                                    Current level:{" "}
                                    {
                                      progressRow
                                        .current_level_key
                                    }
                                  </p>
                                )}

                            </td>


                            {/* -------------------------------------------- */}
                            {/* Progress                                     */}
                            {/* -------------------------------------------- */}

                            <td className="px-6 py-5">

                              <div className="w-full max-w-[190px]">

                                <div className="flex items-center justify-between">

                                  <p className="text-sm font-black text-[#333333]">
                                    {
                                      completedLevels
                                    }
                                    /
                                    {
                                      totalLevels
                                    }
                                  </p>


                                  <p className="text-xs font-bold text-[#757575]">
                                    {Math.round(
                                      progressPercent
                                    )}
                                    %
                                  </p>

                                </div>


                                <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#BDBDBD]">

                                  <div
                                    className="h-full rounded-full bg-[#FF914D]"
                                    style={{
                                      width: `${progressPercent}%`,
                                    }}
                                  />

                                </div>

                              </div>

                            </td>


                            {/* -------------------------------------------- */}
                            {/* Last activity                                */}
                            {/* -------------------------------------------- */}

                            <td className="px-6 py-5">

                              <p className="text-sm font-medium text-[#444444]">
                                {formatRelativeActivity(
                                  progressRow
                                    ?.last_activity_at
                                )}
                              </p>

                            </td>


                            {/* -------------------------------------------- */}
                            {/* Status                                       */}
                            {/* -------------------------------------------- */}

                            <td className="px-6 py-5">

                              <span
                                className={[
                                  "inline-flex rounded-full px-3 py-1 text-xs font-black",

                                  status ===
                                  "Completed"
                                    ? "bg-green-100 text-green-800"
                                    : status ===
                                      "Current"
                                    ? "bg-[#FFF0E6] text-[#E9782F]"
                                    : status ===
                                      "In progress"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-gray-100 text-gray-600",
                                ].join(
                                  " "
                                )}
                              >
                                {
                                  status
                                }
                              </span>

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            </article>
          );
        }
      )}

    </div>
  )}

</section>

      {/* ==================================================================== */}
      {/* Business performance                                                  */}
      {/* ==================================================================== */}

      <section className="mt-10">

        <div className="rounded-[24px] border border-[#D7D7D7] bg-white p-6 shadow-sm md:p-8">

          <div>

            <h2 className="text-[25px] font-black uppercase tracking-[-0.03em] text-[#FF914D] md:text-[30px]">
              Business Performance - Month on Month
            </h2>

            <p className="mt-2 text-sm font-medium text-[#757575]">
              Estimates as reported by the participant
            </p>

          </div>


          <div className="mt-8 space-y-8">

            <BusinessPerformanceEmptyChart
              label="Profit Growth"
            />

            <BusinessPerformanceEmptyChart
              label="Cash Growth"
            />

            <BusinessPerformanceEmptyChart
              label="Staff Growth"
            />

          </div>

        </div>

      </section>


      {/* ==================================================================== */}
      {/* Hidden legacy learner details                                        */}
      {/* ==================================================================== */}

      {SHOW_LEGACY_LEARNER_DETAILS && (
        <>

          {/* ================================================================= */}
          {/* Legacy header                                                    */}
          {/* ================================================================= */}

          <header className="mt-10 rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm md:p-8">

            <div>

              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                Partner · Learner details
              </p>


              <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] md:text-[42px]">
                {detail.full_name ||
                  "Learner"}
              </h1>


              <p className="mt-2 text-sm font-semibold text-[#757575]">
                {detail.email}
              </p>


              <p className="mt-3 text-sm text-[#757575]">
                {detail.partner_name} ·{" "}
                {detail.cohort_name}
              </p>

            </div>


            <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

              <SummaryCard
                label="Current position"
                value={positionLabel(
                  detail,
                  questProgress
                )}
                description={`Updated ${formatDateTime(
                  detail.current_position_updated_at
                )}`}
                icon={BarChart3}
                compact
              />


              <SummaryCard
                label="Completed levels"
                value={`${detail.levels_completed} / ${detail.total_levels}`}
                description={`${progress.toFixed(
                  1
                )}% overall progress`}
                icon={Award}
              />


              <SummaryCard
                label="Total XP"
                value={numberValue(
                  detail.total_xp
                ).toLocaleString(
                  "en-ZA"
                )}
                description="Learning XP recorded"
                icon={Award}
              />


              <SummaryCard
                label="Outstanding tasks"
                value={
                  detail.tasks_outstanding
                }
                description={`${detail.tasks_completed} of ${detail.tasks_total} completed`}
                icon={ListChecks}
              />

            </div>

          </header>


          {/* ================================================================= */}
          {/* Legacy learner/cohort information                                */}
          {/* ================================================================= */}

          <section className="mt-6 grid gap-5 lg:grid-cols-2">

            <DetailSection
              title="Learner and cohort information"
              icon={UserRound}
            >

              <DetailGrid>

                <DetailItem
                  label="Full name"
                  value={
                    detail.full_name
                  }
                />


                <DetailItem
                  label="Email"
                  value={
                    detail.email
                  }
                />


                <DetailItem
                  label="Phone"
                  value={
                    detail.phone
                  }
                />


                <DetailItem
                  label="Cohort"
                  value={
                    detail.cohort_name
                  }
                />


                <DetailItem
                  label="Membership status"
                  value={
                    detail.membership_status
                  }
                />


                <DetailItem
                  label="Joined cohort"
                  value={formatDateTime(
                    detail.joined_at
                  )}
                />


                <DetailItem
                  label="Completed cohort"
                  value={
                    detail.membership_completed_at
                      ? formatDateTime(
                          detail.membership_completed_at
                        )
                      : "Not completed"
                  }
                />


                <DetailItem
                  label="Removed from cohort"
                  value={
                    detail.membership_removed_at
                      ? formatDateTime(
                          detail.membership_removed_at
                        )
                      : "Not removed"
                  }
                />

              </DetailGrid>

            </DetailSection>


            <DetailSection
              title="Participation and activity"
              icon={Activity}
            >

              <DetailGrid>

                <DetailItem
                  label="Last successful login"
                  value={formatDateTime(
                    detail.last_login_at
                  )}
                />


                <DetailItem
                  label="Last app activity"
                  value={formatDateTime(
                    detail.last_app_activity_at
                  )}
                />


                <DetailItem
                  label="Latest recorded activity"
                  value={formatDateTime(
                    detail.last_activity_at
                  )}
                />


                <DetailItem
                  label="Days since activity"
                  value={
                    detail.days_since_activity ===
                    null
                      ? "Not available"
                      : detail.days_since_activity
                  }
                />


                <DetailItem
                  label="Latest completed level"
                  value={
                    detail.last_completed_level_title
                  }
                />


                <DetailItem
                  label="Level completed at"
                  value={formatDateTime(
                    detail.last_completed_at
                  )}
                />


                <DetailItem
                  label="Latest completed task"
                  value={formatDateTime(
                    detail.last_task_completed_at
                  )}
                />


                <DetailItem
                  label="Participation status"
                  value={
                    detail.participation_status ||
                    "Not calculated"
                  }
                />

              </DetailGrid>

            </DetailSection>

          </section>


          {/* ================================================================= */}
          {/* Legacy facilitator assignment                                    */}
          {/* ================================================================= */}

          <DetailSection
            title="Facilitator assignment"
            icon={UserCog}
            className="mt-6"
          >

            {facilitatorLoading ? (
              <p className="text-sm font-semibold text-[#757575]">
                Loading facilitator assignment…
              </p>
            ) : (
              <>

                <div className="grid gap-5 sm:grid-cols-3">

                  <DetailItem
                    label="Current facilitator"
                    value={
                      facilitator
                        ?.full_name ||
                      "Not assigned"
                    }
                  />


                  <DetailItem
                    label="Facilitator email"
                    value={
                      facilitator
                        ?.email ||
                      "Not assigned"
                    }
                  />


                  <DetailItem
                    label="Assigned"
                    value={
                      facilitator
                        ?.assigned_at
                        ? formatDateTime(
                            facilitator.assigned_at
                          )
                        : "Not assigned"
                    }
                  />

                </div>


                {facilitatorError && (
                  <div
                    role="alert"
                    className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
                  >
                    {
                      facilitatorError
                    }
                  </div>
                )}


                {facilitatorMessage && (
                  <div
                    role="status"
                    className="mt-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
                  >
                    {
                      facilitatorMessage
                    }
                  </div>
                )}


                {canManageFacilitator ? (
                  <form
                    onSubmit={
                      handleFacilitatorSave
                    }
                    className="mt-6 rounded-2xl border border-[#EFEFEF] bg-[#FFFDFB] p-5"
                  >

                    <label
                      htmlFor="legacy-learner-facilitator"
                      className="block text-sm font-black text-[#333333]"
                    >
                      Assign facilitator
                    </label>


                    <select
                      id="legacy-learner-facilitator"
                      value={
                        selectedFacilitatorId
                      }
                      onChange={(
                        event
                      ) => {
                        setSelectedFacilitatorId(
                          event.target
                            .value
                        );

                        setFacilitatorMessage(
                          ""
                        );
                      }}
                      disabled={
                        facilitatorSaving
                      }
                      className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:opacity-60"
                    >

                      <option value="">
                        No facilitator assigned
                      </option>


                      {facilitatorOptions.map(
                        (
                          option
                        ) => (
                          <option
                            key={
                              option.partner_member_id
                            }
                            value={
                              option.partner_member_id
                            }
                          >
                            {
                              option.full_name
                            }

                            {option.email
                              ? ` · ${option.email}`
                              : ""}
                          </option>
                        )
                      )}

                    </select>


                    {facilitatorOptions.length ===
                      0 && (
                      <div className="mt-3 rounded-2xl bg-[#FFF7ED] p-4 text-sm leading-relaxed text-[#75523B]">

                        No active facilitators are
                        assigned to this cohort yet.{" "}

                        <Link
                          to={`/partner/cohorts/${detail.cohort_id}`}
                          className="font-black text-[#FF914D] underline"
                        >
                          Manage cohort staff
                        </Link>{" "}

                        and assign a Facilitator to
                        this cohort first.

                      </div>
                    )}


                    <div className="mt-4 flex flex-wrap items-center gap-3">

                      <button
                        type="submit"
                        disabled={
                          facilitatorSaving
                        }
                        className="rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {facilitatorSaving
                          ? "Saving…"
                          : "Save facilitator"}
                      </button>


                      {facilitator && (
                        <span className="text-xs font-semibold text-[#757575]">
                          Choose “No facilitator
                          assigned” and save to
                          remove the current
                          assignment.
                        </span>
                      )}

                    </div>

                  </form>
                ) : (
                  <p className="mt-5 text-sm leading-relaxed text-[#757575]">
                    This facilitator assignment is
                    read-only for your partner
                    role.
                  </p>
                )}

              </>
            )}

          </DetailSection>


          {/* ================================================================= */}
          {/* Legacy quest-by-quest progress                                   */}
          {/* ================================================================= */}

          <DetailSection
            title="Quest-by-quest progress"
            icon={BarChart3}
            className="mt-6"
          >

            {questProgress.length ===
            0 ? (
              <EmptyMessage>
                No quest progress has been
                recorded.
              </EmptyMessage>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">

                {questProgress.map(
                  (quest) => {
                    const questPercent =
                      numberValue(
                        quest.progress_percent
                      );


                    return (
                      <article
                        key={
                          quest.quest_id
                        }
                        className={[
                          "rounded-[22px] border p-5",

                          quest.is_current_quest
                            ? "border-[#FF914D] bg-[#FFF9F5]"
                            : "border-[#EFEFEF] bg-white",
                        ].join(" ")}
                      >

                        <div className="flex items-start justify-between gap-3">

                          <div className="min-w-0">

                            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                              Quest{" "}
                              {
                                quest.quest_id
                              }
                            </p>


                            <h3 className="mt-2 break-words text-lg font-black leading-snug text-[#333333]">
                              {quest.quest_title ||
                                "Learning pathway"}
                            </h3>


                            <p className="mt-3 text-2xl font-black">
                              {
                                quest.completed_levels
                              }{" "}
                              /{" "}
                              {
                                quest.total_levels
                              }
                            </p>


                            <p className="mt-1 text-xs font-semibold text-[#757575]">
                              Levels completed
                            </p>

                          </div>


                          {quest.is_current_quest && (
                            <span className="rounded-full bg-[#FF914D] px-3 py-1 text-xs font-black text-white">
                              Current
                            </span>
                          )}

                        </div>


                        <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#EFEFEF]">

                          <div
                            className="h-full rounded-full bg-[#FF914D]"
                            style={{
                              width: `${Math.min(
                                Math.max(
                                  questPercent,
                                  0
                                ),
                                100
                              )}%`,
                            }}
                          />

                        </div>


                        <div className="mt-3 flex items-center justify-between text-xs font-semibold text-[#757575]">

                          <span>
                            {questPercent.toFixed(
                              1
                            )}
                            %
                          </span>


                          <span>
                            {numberValue(
                              quest.xp_earned
                            ).toLocaleString(
                              "en-ZA"
                            )}{" "}
                            XP
                          </span>

                        </div>


                        <p className="mt-4 text-sm font-black">
                          {quest.last_completed_level_title ||
                            "No completed levels"}
                        </p>


                        <p className="mt-1 text-xs text-[#757575]">
                          {formatDateTime(
                            quest.last_completed_at
                          )}
                        </p>

                      </article>
                    );
                  }
                )}

              </div>
            )}

          </DetailSection>


          {/* ================================================================= */}
          {/* Legacy practical tasks                                           */}
          {/* ================================================================= */}

          <DetailSection
            title="Practical tasks"
            icon={ListChecks}
            className="mt-6"
          >

            {tasks.length ===
            0 ? (
              <EmptyMessage>
                No practical tasks have been
                unlocked.
              </EmptyMessage>
            ) : (
              <div className="overflow-x-auto">

                <table className="min-w-[920px] divide-y divide-[#EFEFEF]">

                  <thead className="bg-[#FFF9F5]">

                    <tr>

                      <TableHeading>
                        Task
                      </TableHeading>


                      <TableHeading>
                        Level
                      </TableHeading>


                      <TableHeading>
                        Status
                      </TableHeading>


                      <TableHeading>
                        Completed
                      </TableHeading>


                      <TableHeading>
                        XP
                      </TableHeading>


                      <TableHeading>
                        Submission
                      </TableHeading>

                    </tr>

                  </thead>


                  <tbody className="divide-y divide-[#EFEFEF]">

                    {tasks.map(
                      (task) => (
                        <tr
                          key={
                            task.practical_challenge_id
                          }
                        >

                          <td className="px-5 py-4">

                            <p className="font-black">
                              {
                                task.title
                              }
                            </p>


                            <p className="mt-1 max-w-md text-xs leading-relaxed text-[#757575]">
                              {
                                task.instructions
                              }
                            </p>

                          </td>


                          <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold">
                            Quest{" "}
                            {task.quest_id} ·{" "}
                            {
                              task.level_key
                            }
                          </td>


                          <td className="px-5 py-4">

                            <span
                              className={[
                                "inline-flex rounded-full px-3 py-1 text-xs font-black capitalize",

                                statusClasses(
                                  task.task_status
                                ),
                              ].join(" ")}
                            >
                              {
                                task.task_status
                              }
                            </span>

                          </td>


                          <td className="whitespace-nowrap px-5 py-4 text-sm text-[#757575]">
                            {formatDateTime(
                              task.completed_at
                            )}
                          </td>


                          <td className="px-5 py-4 font-black">
                            {numberValue(
                              task.xp_earned
                            )}
                          </td>


                          <td className="px-5 py-4 text-sm font-semibold">
                            {task.has_submission
                              ? "Submitted"
                              : "Not submitted"}
                          </td>

                        </tr>
                      )
                    )}

                  </tbody>

                </table>

              </div>
            )}

          </DetailSection>


          {/* ================================================================= */}
          {/* Legacy business profile                                          */}
          {/* ================================================================= */}

          <DetailSection
            title="Business profile"
            icon={BriefcaseBusiness}
            className="mt-6"
          >

            <DetailGrid>

              <DetailItem
                label="Business name"
                value={
                  detail.business_name
                }
              />


              <DetailItem
                label="Industry"
                value={
                  detail.industry
                }
              />


              <DetailItem
                label="Legal nature"
                value={
                  detail.legal_nature
                }
              />


              <DetailItem
                label="Registration number"
                value={
                  detail.registration_number
                }
              />


              <DetailItem
                label="CIPC registered"
                value={displayBoolean(
                  detail.cipc_registered
                )}
              />


              <DetailItem
                label="Business age"
                value={
                  detail.business_age
                }
              />


              <DetailItem
                label="Monthly revenue range"
                value={
                  detail.monthly_revenue_range
                }
              />


              <DetailItem
                label="Province"
                value={
                  detail.province
                }
              />


              <DetailItem
                label="Address"
                value={
                  detail.business_address
                }
              />


              <DetailItem
                label="Reason for using Gwin"
                value={
                  detail.app_reason
                }
              />

            </DetailGrid>

          </DetailSection>


          {/* ================================================================= */}
          {/* Legacy behaviour / risk                                          */}
          {/* ================================================================= */}

          <section className="mt-6 grid gap-5 lg:grid-cols-2">

            <DetailSection
              title="Behaviour indicators"
              icon={Activity}
            >

              <DetailGrid>

                <DetailItem
                  label="Behavioural score"
                  value={
                    detail.behavioural_score ??
                    "Not calculated"
                  }
                />


                <DetailItem
                  label="Behavioural status"
                  value={
                    detail.behavioural_status ||
                    "Not calculated"
                  }
                />

              </DetailGrid>


              <p className="mt-4 text-sm leading-relaxed text-[#757575]">
                Behaviour indicators have not
                yet been defined. Missing data is
                not treated as poor behaviour.
              </p>

            </DetailSection>


            <DetailSection
              title="Risk indicators"
              icon={ShieldAlert}
            >

              <DetailGrid>

                <DetailItem
                  label="Active risk flags"
                  value={
                    detail.active_risk_flag_count ??
                    "Not available"
                  }
                />


                <DetailItem
                  label="Risk status"
                  value={
                    detail.risk_status ||
                    "Not available"
                  }
                />

              </DetailGrid>


              <p className="mt-4 text-sm leading-relaxed text-[#757575]">
                Risk rules have not yet been
                implemented. No risk conclusion
                is made from missing data.
              </p>

            </DetailSection>

          </section>


          {/* ================================================================= */}
          {/* Legacy financial metrics                                         */}
          {/* ================================================================= */}

          <PartnerLearnerFinancialMetrics
            cohortLearnerId={
              cohortLearnerId
            }
          />

        </>
      )}


      {/* ==================================================================== */}
      {/* Facilitator manager popup                                            */}
      {/* ==================================================================== */}

      {facilitatorManagerOpen &&
        canManageFacilitator && (
          <div
            role="presentation"
            onMouseDown={(
              event
            ) => {
              if (
                event.target ===
                event.currentTarget &&
                !facilitatorSaving
              ) {
                setFacilitatorManagerOpen(
                  false
                );
              }
            }}
            className="fixed inset-0 z-[150] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"
          >

            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="facilitator-manager-title"
              className="max-h-[90vh] w-full overflow-y-auto rounded-t-[28px] bg-white shadow-2xl sm:max-w-lg sm:rounded-[28px]"
            >

              <header className="flex items-start justify-between border-b border-[#EAEAEA] px-6 py-5">

                <div>

                  <p className="text-xs font-black uppercase tracking-[0.12em] text-[#FF914D]">
                    Entrepreneur
                  </p>


                  <h2
                    id="facilitator-manager-title"
                    className="mt-1 text-2xl font-black tracking-[-0.025em] text-[#333333]"
                  >
                    Manage facilitator
                  </h2>

                </div>


                <button
                  type="button"
                  disabled={
                    facilitatorSaving
                  }
                  onClick={() =>
                    setFacilitatorManagerOpen(
                      false
                    )
                  }
                  aria-label="Close facilitator manager"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-[#777777] transition hover:bg-[#F3F3F3] hover:text-black disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>

              </header>


              <form
                onSubmit={
                  handleFacilitatorSave
                }
                className="p-6"
              >

                <div className="rounded-[18px] border border-[#EFEFEF] bg-[#F8F8F8] p-4">

                  <p className="font-black text-[#333333]">
                    {profile.full_name ||
                      "Entrepreneur"}
                  </p>


                  <p className="mt-1 text-sm text-[#757575]">
                    {
                      profile.cohort_name
                    }
                  </p>

                </div>


                {facilitatorError && (
                  <div
                    role="alert"
                    className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
                  >
                    {
                      facilitatorError
                    }
                  </div>
                )}


                {facilitatorMessage && (
                  <div
                    role="status"
                    className="mt-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
                  >
                    {
                      facilitatorMessage
                    }
                  </div>
                )}


                <div className="mt-5">

                  <label
                    htmlFor="simplified-learner-facilitator"
                    className="block text-sm font-black text-[#333333]"
                  >
                    Facilitator
                  </label>


                  <select
                    id="simplified-learner-facilitator"
                    value={
                      selectedFacilitatorId
                    }
                    onChange={(
                      event
                    ) => {
                      setSelectedFacilitatorId(
                        event.target
                          .value
                      );

                      setFacilitatorMessage(
                        ""
                      );
                    }}
                    disabled={
                      facilitatorSaving
                    }
                    className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:opacity-60"
                  >

                    <option value="">
                      No facilitator assigned
                    </option>


                    {facilitatorOptions.map(
                      (
                        option
                      ) => (
                        <option
                          key={
                            option.partner_member_id
                          }
                          value={
                            option.partner_member_id
                          }
                        >
                          {
                            option.full_name
                          }

                          {option.email
                            ? ` · ${option.email}`
                            : ""}
                        </option>
                      )
                    )}

                  </select>

                </div>


                {facilitatorOptions.length ===
                  0 && (
                  <div className="mt-4 rounded-2xl border border-[#FFD7BE] bg-[#FFF7F1] p-4 text-sm leading-relaxed text-[#75523B]">

                    No active Facilitators are
                    assigned to this cohort yet.{" "}

                    <Link
                      to={`/partner/cohorts/${profile.cohort_id}`}
                      className="font-black text-[#FF914D] underline"
                    >
                      Manage cohort staff
                    </Link>{" "}

                    first.

                  </div>
                )}


                <div className="mt-6 flex flex-col-reverse gap-3 border-t border-[#EFEFEF] pt-5 sm:flex-row sm:justify-end">

                  <button
                    type="button"
                    disabled={
                      facilitatorSaving
                    }
                    onClick={() => {
                      setSelectedFacilitatorId(
                        facilitator
                          ?.partner_member_id ||
                          ""
                      );

                      setFacilitatorMessage(
                        ""
                      );

                      setFacilitatorManagerOpen(
                        false
                      );
                    }}
                    className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black text-[#555555] transition hover:bg-[#F7F7F7] disabled:opacity-50"
                  >
                    Cancel
                  </button>


                  <button
                    type="submit"
                    disabled={
                      facilitatorSaving
                    }
                    className="rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {facilitatorSaving
                      ? "Saving…"
                      : "Save facilitator"}
                  </button>

                </div>

              </form>

            </div>

          </div>
        )}

    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Simplified profile field                                                   */
/* -------------------------------------------------------------------------- */

function SimpleProfileField({
  label,
  value,
}) {
  return (
    <div>

      <p className="text-[11px] font-black uppercase tracking-[0.05em] text-[#777777]">
        {label}
      </p>


      <p className="mt-1.5 break-words text-sm font-semibold leading-relaxed text-[#333333]">
        {displayValue(
          value
        )}
      </p>

    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Simplified summary card                                                    */
/* -------------------------------------------------------------------------- */

function SimpleSummaryCard({
  children,
}) {
  return (
    <article className="min-h-[180px] rounded-[22px] border border-[#D7D7D7] bg-white p-6 shadow-sm">
      {children}
    </article>
  );
}


function SimpleSummaryHeader({
  icon: Icon,
  label,
}) {
  return (
    <div className="flex items-center gap-3">

      <div className="flex h-8 w-8 flex-none items-center justify-center text-[#FF914D]">
        <Icon className="h-5 w-5" />
      </div>


      <p className="text-sm font-semibold text-[#757575]">
        {label}
      </p>

    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Legacy summary card                                                        */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  description,
  icon: Icon,
  compact = false,
}) {
  return (
    <article className="rounded-[22px] border border-[#EFEFEF] bg-[#FFFDFB] p-5">

      <div className="flex items-start justify-between gap-4">

        <div>

          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>


          <p
            className={[
              "mt-3 font-black",

              compact
                ? "text-lg"
                : "text-3xl",
            ].join(" ")}
          >
            {value}
          </p>


          <p className="mt-1 text-sm text-[#757575]">
            {description}
          </p>

        </div>


        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">

          <Icon className="h-5 w-5" />

        </div>

      </div>

    </article>
  );
}


/* -------------------------------------------------------------------------- */
/* Legacy detail section                                                      */
/* -------------------------------------------------------------------------- */

function DetailSection({
  title,
  icon: Icon,
  children,
  className = "",
}) {
  return (
    <section
      className={[
        "rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm md:p-6",

        className,
      ].join(" ")}
    >

      <div className="flex items-center gap-3">

        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">

          <Icon className="h-5 w-5" />

        </div>


        <h2 className="text-lg font-black">
          {title}
        </h2>

      </div>


      <div className="mt-5">
        {children}
      </div>

    </section>
  );
}


/* -------------------------------------------------------------------------- */
/* Legacy detail grid                                                         */
/* -------------------------------------------------------------------------- */

function DetailGrid({
  children,
}) {
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {children}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Legacy detail item                                                         */
/* -------------------------------------------------------------------------- */

function DetailItem({
  label,
  value,
}) {
  return (
    <div>

      <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
        {label}
      </p>


      <p className="mt-1 break-words font-black text-[#333333]">
        {displayValue(
          value
        )}
      </p>

    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Legacy empty state                                                         */
/* -------------------------------------------------------------------------- */

function EmptyMessage({
  children,
}) {
  return (
    <div className="rounded-2xl border border-dashed border-[#DDDDDD] bg-[#FAFAFA] p-6 text-center text-sm font-semibold text-[#757575]">
      {children}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Legacy table heading                                                       */
/* -------------------------------------------------------------------------- */

function TableHeading({
  children,
}) {
  return (
    <th
      scope="col"
      className="whitespace-nowrap px-5 py-3 text-left text-xs font-black uppercase tracking-wider text-[#757575]"
    >
      {children}
    </th>
  );
}


function BusinessPerformanceEmptyChart({
  label,
}) {
  return (
    <div className="grid gap-4 md:grid-cols-[150px_minmax(0,1fr)] md:items-center">

      <div>
        <p className="text-sm font-black uppercase tracking-[0.02em] text-[#666666]">
          {label}
        </p>
      </div>


      <div className="relative min-h-[150px] overflow-hidden rounded-[18px] border border-dashed border-[#D7D7D7] bg-[#FAFAFA] px-5 py-5">

        {/* -------------------------------------------------------------- */}
        {/* Subtle chart guide lines                                       */}
        {/* -------------------------------------------------------------- */}

        <div
          aria-hidden="true"
          className="absolute inset-x-5 top-[25%] border-t border-[#ECECEC]"
        />

        <div
          aria-hidden="true"
          className="absolute inset-x-5 top-[50%] border-t border-[#ECECEC]"
        />

        <div
          aria-hidden="true"
          className="absolute inset-x-5 top-[75%] border-t border-[#ECECEC]"
        />


        {/* -------------------------------------------------------------- */}
        {/* Empty state                                                    */}
        {/* -------------------------------------------------------------- */}

        <div className="relative z-10 flex min-h-[88px] items-center justify-center text-center">

          <div>

            <p className="text-sm font-black text-[#333333]">
              No data recorded yet
            </p>

            <p className="mt-1 max-w-md text-xs leading-relaxed text-[#757575]">
              Monthly performance will appear here once
              the entrepreneur starts submitting business
              performance information.
            </p>

          </div>

        </div>


        {/* -------------------------------------------------------------- */}
        {/* Month axis                                                     */}
        {/* -------------------------------------------------------------- */}

        <div className="relative z-10 mt-2 grid grid-cols-12 gap-1">

          {Array.from(
            {
              length: 12,
            },
            (_, index) => (
              <span
                key={index}
                className="text-center text-[10px] font-bold text-[#A0A0A0]"
              >
                {index + 1}
              </span>
            )
          )}

        </div>

      </div>

    </div>
  );
}